/**
 * Admin Layout — top-level chrome for the /admin section.
 *
 * Renders a TabList that switches between admin sub-pages (Roles, User
 * Activity). The active tab is derived from the current URL so deep links
 * land on the right tab.
 */

import { useNavigate, useLocation, Navigate, Outlet } from 'react-router-dom';
import {
  TabList,
  Tab,
  makeStyles,
  tokens,
  type SelectTabData,
  type SelectTabEvent,
} from '@fluentui/react-components';
import { veracityColors } from '@shared/ui';
import { useAuth } from '@shared/providers/AuthProvider';
import { hasGlobalAdmin } from '@shared/auth';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 64px)',
  },
  tabBar: {
    padding: `0 ${tokens.spacingHorizontalXL}`,
    backgroundColor: '#FFFFFF',
    borderBottom: `1px solid ${veracityColors.firstLight}`,
  },
  body: {
    flex: 1,
  },
});

type TabKey = 'roles' | 'activity' | 'guide';

function tabFromPath(pathname: string): TabKey {
  if (pathname.startsWith('/admin/activity')) return 'activity';
  if (pathname.startsWith('/admin/guide')) return 'guide';
  return 'roles';
}

export function AdminLayout() {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { roles } = useAuth();
  const active = tabFromPath(location.pathname);

  if (!hasGlobalAdmin(roles)) {
    return <Navigate to="/" replace />;
  }

  const onSelect = (_: SelectTabEvent, data: SelectTabData) => {
    const next = data.value as TabKey;
    if (next === 'activity') navigate('/admin/activity');
    else if (next === 'guide') navigate('/admin/guide');
    else navigate('/admin');
  };

  return (
    <div className={styles.root}>
      <div className={styles.tabBar}>
        <TabList selectedValue={active} onTabSelect={onSelect}>
          <Tab value="roles">Roles</Tab>
          <Tab value="activity">User Activity</Tab>
          <Tab value="guide">Guide</Tab>
        </TabList>
      </div>
      <div className={styles.body}>
        <Outlet />
      </div>
    </div>
  );
}

export default AdminLayout;
